import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { MoneyInput } from '../components/MoneyInput';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Briefcase, Plus, Pencil, History, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { fmtDateTime } from '../lib/datetime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const IVA_OPTIONS = [
  { v: '21', label: 'IVA 21%' },
  { v: '10.5', label: 'IVA 10,5%' },
  { v: 'exento', label: 'Exento' },
  { v: 'no_alcanzado', label: 'No alcanzado' },
];

const empty = {
  code: '', name: '', description: '',
  billing_type: 'abono', talonario: 'proforma', period_mode: 'adelantado',
  iva_rate: '21', current_price: 0, income_concept_id: '',
  service_class: 'adicional', emission_mode: 'nota_debito',
  unit_measure_code: '98',
};

export const SERVICE_CLASSES = [
  { v: 'prestacional', label: 'Prestacional', short: 'Prestacional', cls: 'bg-indigo-100 text-indigo-800',
    doc: 'Siempre FACTURA. Se factura al receptor del convenio del residente (la obra social o el residente).' },
  { v: 'adicional', label: 'Adicional', short: 'Adicional', cls: 'bg-violet-100 text-violet-800',
    doc: 'Puede salir como FACTURA o como NOTA DE DÉBITO asociada a la última factura.' },
];

const CLASS_DEFAULTS = {
  prestacional: { emission_mode: 'factura' },
  adicional: { emission_mode: 'nota_debito' },
};

function userCan(user, mod, act) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return user.permissions?.[mod]?.[act] === true;
}

function Services() {
  const { user } = useOutletContext();
  const [list, setList] = useState([]);
  const [concepts, setConcepts] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [dialog, setDialog] = useState({ open: false, data: empty });
  const [bulkDlg, setBulkDlg] = useState({ open: false, pct: '', round_to: '', reason: '', service_ids: [], apply_to_all: true });
  const [bulkPreview, setBulkPreview] = useState(null);
  const [updates, setUpdates] = useState(null);
  const [insurers, setInsurers] = useState([]);
  const [historyDlg, setHistoryDlg] = useState({ open: false, list: [], service: null });
  const [saving, setSaving] = useState(false);

  const canCreate = userCan(user, 'servicios_residentes', 'crear');
  const canEdit = userCan(user, 'servicios_residentes', 'editar');
  const canPriceUpd = userCan(user, 'servicios_residentes', 'actualizar_precios');

  const fetchAll = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/resident-services?include_inactive=${includeInactive}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/treasury/income-concepts`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setList(s.data || []);
      setConcepts(c.data || []);
    } catch (e) {
      toast.error('Error al cargar catálogo');
    }
  }, [includeInactive]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    loadUpdates();
    axios.get(`${BACKEND_URL}/api/health-insurers`, { withCredentials: true })
      .then(r => setInsurers(r.data || [])).catch(() => setInsurers([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => setDialog({ open: true, data: { ...empty } });
  const openEdit = (s) => setDialog({ open: true, data: { ...s } });

  const save = async () => {
    const d = dialog.data;
    if (!d.name || !d.income_concept_id || d.current_price < 0) { toast.error('Completá nombre, concepto y precio'); return; }
    setSaving(true);
    try {
      if (d.service_id) {
        await axios.put(`${BACKEND_URL}/api/resident-services/${d.service_id}`, d, { withCredentials: true });
      } else {
        await axios.post(`${BACKEND_URL}/api/resident-services`, d, { withCredentials: true });
      }
      toast.success('Guardado'); setDialog({ open: false, data: empty }); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setSaving(false); }
  };

  const bulkPayload = () => ({
    delta_pct: parseFloat(bulkDlg.pct),
    apply_to_all: bulkDlg.apply_to_all,
    service_ids: bulkDlg.apply_to_all ? [] : bulkDlg.service_ids,
    insurer_ids: bulkDlg.apply_to_all ? [] : (bulkDlg.insurer_ids || []),
    reason: bulkDlg.reason,
    round_to: bulkDlg.round_to ? parseInt(bulkDlg.round_to) : null,
    effective_from: bulkDlg.effective_from || null,
  });

  const loadUpdates = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/resident-services/price-updates`, { withCredentials: true });
      setUpdates(r.data);
    } catch { setUpdates(null); }
  };

  const cancelScheduled = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/resident-services/price-updates/${id}`, { withCredentials: true });
      toast.success('Ajuste programado cancelado');
      loadUpdates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const revertBulk = async (id) => {
    if (!window.confirm('¿Deshacer esta actualización? Se restituye el precio exacto anterior de cada concepto.')) return;
    try {
      const r = await axios.post(`${BACKEND_URL}/api/resident-services/price-updates/${id}/revert`, {}, { withCredentials: true });
      toast.success(`${r.data.reverted} precios restituidos` + (r.data.skipped ? ` · ${r.data.skipped} salteados por cambios posteriores` : ''));
      fetchAll(); loadUpdates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const previewBulk = async () => {
    if (!bulkDlg.pct || isNaN(parseFloat(bulkDlg.pct))) { toast.error('% inválido'); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/resident-services/price-update/preview`,
        bulkPayload(), { withCredentials: true });
      setBulkPreview(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al calcular la vista previa'); }
    finally { setSaving(false); }
  };

  const closeBulk = () => {
    setBulkDlg({ open: false, pct: '', round_to: '', reason: '', service_ids: [], insurer_ids: [], apply_to_all: true, effective_from: '' });
    setBulkPreview(null);
  };

  const applyBulk = async () => {
    if (!bulkDlg.pct || isNaN(parseFloat(bulkDlg.pct))) { toast.error('% inválido'); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/resident-services/price-update`,
        bulkPayload(), { withCredentials: true });
      if (r.data.scheduled) {
        toast.success(`Ajuste programado para el ${r.data.effective_from}: ${r.data.services_count} servicios`);
      } else {
        toast.success(`${r.data.updated} servicios y ${r.data.assignments_updated || 0} precios propios de fichas actualizados`);
      }
      closeBulk();
      fetchAll();
      loadUpdates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setSaving(false); }
  };

  const openHistory = async (s) => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/resident-services/${s.service_id}/price-history`, { withCredentials: true });
      setHistoryDlg({ open: true, list: r.data, service: s });
    } catch (e) { toast.error('Error'); }
  };

  const remove = async (s) => {
    if (!window.confirm(`¿Desactivar "${s.name}"?`)) return;
    try { await axios.delete(`${BACKEND_URL}/api/resident-services/${s.service_id}`, { withCredentials: true }); toast.success('Desactivado'); fetchAll(); }
    catch (e) { toast.error('Error'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold flex items-center gap-2"><Briefcase className="w-7 h-7 text-indigo-500" /> Catálogo de Servicios</h1>
        <div className="flex gap-2">
          {canPriceUpd && <Button variant="outline" onClick={() => setBulkDlg({ ...bulkDlg, open: true })} data-testid="bulk-update-btn"><TrendingUp className="w-4 h-4 mr-2" />Actualizar precios</Button>}
          {canCreate && <Button onClick={openNew} data-testid="new-service-btn"><Plus className="w-4 h-4 mr-2" />Nuevo servicio</Button>}
        </div>
      </div>

      <Card>
        {canPriceUpd && (updates?.scheduled?.length > 0 || updates?.applied?.length > 0) && (
        <Card data-testid="price-updates-panel">
          <CardHeader className="pb-2"><CardTitle className="text-base">Actualizaciones de precios</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {updates.scheduled?.length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-2 space-y-1" data-testid="scheduled-updates">
                <p className="text-xs font-semibold text-amber-900 dark:text-amber-200">Programadas (se aplican solas)</p>
                {updates.scheduled.map(u => (
                  <div key={u.bulk_update_id} className="flex items-center justify-between gap-2 text-xs">
                    <span>
                      <b>{u.delta_pct > 0 ? '+' : ''}{u.delta_pct}%</b> desde <b>{u.effective_from}</b> ·
                      {' '}{u.services_count} servicio/s y {u.assignments_count} precio/s de ficha
                      {u.reason ? ` · ${u.reason}` : ''}
                      <span className="text-muted-foreground"> · cargada por {u.created_by_name}</span>
                    </span>
                    <Button size="sm" variant="outline" onClick={() => cancelScheduled(u.bulk_update_id)}
                            data-testid={`cancel-scheduled-${u.bulk_update_id}`}>Cancelar</Button>
                  </div>
                ))}
              </div>
            )}
            {updates.applied?.length > 0 && (
              <div className="space-y-1" data-testid="applied-updates">
                <p className="text-xs font-semibold text-muted-foreground">Últimas aplicadas</p>
                {updates.applied.map(u => (
                  <div key={u.bulk_update_id} className="flex items-center justify-between gap-2 text-xs border-b py-1">
                    <span>
                      <b>{u.delta_pct > 0 ? '+' : ''}{u.delta_pct}%</b> · {u.services_count} servicio/s y {u.assignments_count} precio/s de ficha
                      {u.reason ? ` · ${u.reason}` : ''}
                      <span className="text-muted-foreground"> · {u.applied_at ? new Date(u.applied_at).toLocaleString('es-AR') : ''} por {u.applied_by_name || u.created_by_name}</span>
                      {u.status === 'reverted' && <Badge className="ml-2 text-[10px] bg-slate-200 text-slate-700">revertida</Badge>}
                    </span>
                    {u.status !== 'reverted' && (
                      <Button size="sm" variant="outline" onClick={() => revertBulk(u.bulk_update_id)}
                              data-testid={`revert-${u.bulk_update_id}`}>Deshacer</Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Servicios ({list.length})</CardTitle>
            <label className="text-xs flex items-center gap-1 cursor-pointer">
              <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} /> Mostrar inactivos
            </label>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Código</TableHead><TableHead>Nombre</TableHead><TableHead>Tipo</TableHead><TableHead>Esquema</TableHead><TableHead>Talonario</TableHead><TableHead>IVA</TableHead>
              <TableHead className="text-right">Precio</TableHead><TableHead>Concepto Ingreso</TableHead><TableHead className="text-right">Acciones</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {list.map(s => (
                <TableRow key={s.service_id} className={s.is_active === false ? 'opacity-50' : ''} data-testid={`svc-row-${s.service_id}`}>
                  <TableCell className="font-mono text-xs">{s.code || '—'}</TableCell>
                  <TableCell><span className="font-medium">{s.name}</span>{s.description && <div className="text-xs text-muted-foreground">{s.description}</div>}</TableCell>
                  <TableCell><Badge className={s.billing_type === 'abono' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}>{s.billing_type}</Badge></TableCell>
                  <TableCell>
                    {(() => {
                      const c = SERVICE_CLASSES.find(x => x.v === s.service_class) || SERVICE_CLASSES[1];
                      return <Badge className={c.cls} data-testid={`svc-class-${s.service_id}`}>{s.emission_mode === 'nota_debito' ? 'ND' : 'FC'} · {c.short}</Badge>;
                    })()}
                  </TableCell>
                  <TableCell><Badge variant="outline">{s.talonario.toUpperCase()}</Badge></TableCell>
                  <TableCell><Badge variant="outline">{IVA_OPTIONS.find(i => i.v === s.iva_rate)?.label || s.iva_rate}</Badge></TableCell>
                  <TableCell className="text-right tabular-nums">${(s.current_price || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</TableCell>
                  <TableCell className="text-xs">{s.income_concept_name || '—'}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => openHistory(s)} title="Historial precios"><History className="w-4 h-4" /></Button>
                    {canEdit && <Button variant="ghost" size="icon" onClick={() => openEdit(s)}><Pencil className="w-4 h-4" /></Button>}
                    {canEdit && s.is_active !== false && <Button variant="ghost" size="sm" onClick={() => remove(s)} className="text-red-600">Desactivar</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog crear/editar */}
      <Dialog open={dialog.open} onOpenChange={(o) => !o && setDialog({ open: false, data: empty })}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{dialog.data.service_id ? 'Editar' : 'Nuevo'} servicio</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Código</Label><Input value={dialog.data.code || ''} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, code: e.target.value } })} /></div>
              <div className="col-span-2"><Label>Nombre *</Label><Input value={dialog.data.name} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, name: e.target.value } })} /></div>
            </div>
            <div><Label>Descripción</Label><Textarea rows={2} value={dialog.data.description || ''} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, description: e.target.value } })} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Tipo</Label>
                <select value={dialog.data.billing_type} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, billing_type: e.target.value } })} className="flex h-10 w-full rounded-md border px-2 text-sm">
                  <option value="abono">Abono mensual</option><option value="puntual">Puntual</option>
                </select></div>
              <div><Label>Talonario</Label>
                <select value={dialog.data.talonario} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, talonario: e.target.value } })} className="flex h-10 w-full rounded-md border px-2 text-sm">
                  <option value="proforma">PROFORMA</option><option value="arca">ARCA</option>
                </select></div>
              <div><Label>IVA</Label>
                <select value={dialog.data.iva_rate} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, iva_rate: e.target.value } })} className="flex h-10 w-full rounded-md border px-2 text-sm">
                  {IVA_OPTIONS.map(i => <option key={i.v} value={i.v}>{i.label}</option>)}
                </select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Clase de concepto *</Label>
                <select value={dialog.data.service_class || 'adicional'}
                        onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, service_class: e.target.value, ...CLASS_DEFAULTS[e.target.value] } })}
                        className="flex h-10 w-full rounded-md border px-2 text-sm" data-testid="service-class">
                  {SERVICE_CLASSES.map(c => <option key={c.v} value={c.v}>{c.label}</option>)}
                </select></div>
              <div><Label>Comprobante que emite *</Label>
                {dialog.data.service_class === 'prestacional' ? (
                  <div className="flex h-10 items-center rounded-md border bg-muted px-2 text-sm" data-testid="service-emission-locked">
                    Factura (fijo para los prestacionales)
                  </div>
                ) : (
                  <select value={dialog.data.emission_mode || 'nota_debito'}
                          onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, emission_mode: e.target.value } })}
                          className="flex h-10 w-full rounded-md border px-2 text-sm" data-testid="service-emission-mode">
                    <option value="nota_debito">Nota de Débito</option>
                    <option value="factura">Factura</option>
                  </select>
                )}
              </div>
            </div>
            <div className="rounded-md border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-2 text-xs text-emerald-800 dark:text-emerald-200" data-testid="service-class-hint">
              {SERVICE_CLASSES.find(c => c.v === (dialog.data.service_class || 'adicional'))?.doc}
            </div>
            <div><Label>U.Medida (código AFIP para facturas ARCA)</Label>
              <select value={dialog.data.unit_measure_code || '98'}
                      onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, unit_measure_code: e.target.value } })}
                      className="flex h-10 w-full rounded-md border px-2 text-sm" data-testid="service-unit-measure">
                <option value="07">07 · unidades</option>
                <option value="98">98 · otras unidades</option>
                <option value="99">99 · servicio</option>
                <option value="01">01 · kg</option>
                <option value="02">02 · metros</option>
                <option value="05">05 · litros</option>
                <option value="09">09 · docenas</option>
                <option value="14">14 · gramos</option>
                <option value="68">68 · ml</option>
                <option value="76">76 · número</option>
              </select>
              <p className="text-[11px] text-muted-foreground mt-1">Aparece en la columna U.Medida del PDF y en el TXT que exige PAMI. Default: 98 (otras unidades).</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label>Modalidad de período (ARCA)</Label>
                <select value={dialog.data.period_mode || 'adelantado'} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, period_mode: e.target.value } })} className="flex h-10 w-full rounded-md border px-2 text-sm" data-testid="service-period-mode">
                  <option value="adelantado">Adelantado (prepago)</option><option value="vencido">Mes vencido (Obras Sociales)</option>
                </select></div>
              <div className="flex items-end"><p className="text-xs text-muted-foreground">Define el período de servicio que se informa a ARCA: <b>adelantado</b> = mes en curso (prepago); <b>vencido</b> = mes ya transcurrido (típico OS).</p></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Precio *</Label><MoneyInput value={dialog.data.current_price} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, current_price: parseFloat(e.target.value) || 0 } })} /></div>
              <div><Label>Concepto de Ingreso *</Label>
                <select value={dialog.data.income_concept_id} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, income_concept_id: e.target.value } })} className="flex h-10 w-full rounded-md border px-2 text-sm">
                  <option value="">Seleccionar...</option>
                  {concepts.map(c => <option key={c.concept_id} value={c.concept_id}>{c.name}</option>)}
                </select></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, data: empty })}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="save-service-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Actualizar precios */}
      <Dialog open={bulkDlg.open} onOpenChange={(o) => !o && closeBulk()}>
        <DialogContent className={bulkPreview ? 'max-w-3xl max-h-[90vh] overflow-y-auto' : ''}>
          <DialogHeader><DialogTitle>
            Actualizar precios masivo {bulkPreview ? '· Vista previa' : '· Configuración'}
          </DialogTitle></DialogHeader>
          {bulkPreview ? (
            <div className="space-y-4" data-testid="bulk-preview">
              <div className="rounded-md border bg-muted/40 p-3 text-sm">
                Ajuste <b>{bulkPreview.delta_pct > 0 ? '+' : ''}{bulkPreview.delta_pct}%</b>
                {bulkPreview.round_to ? <> · redondeo a múltiplos de {bulkPreview.round_to}</> : null} ·
                <b> {bulkPreview.summary.services}</b> servicio(s) del catálogo y
                <b> {bulkPreview.summary.assignments}</b> precio(s) propio(s) en fichas de residentes.
              </div>

              <div>
                <p className="text-sm font-medium mb-1">Catálogo de servicios</p>
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="text-xs">Servicio</TableHead>
                    <TableHead className="text-xs text-right">Precio actual</TableHead>
                    <TableHead className="text-xs text-right">Precio nuevo</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {bulkPreview.services.map(r => (
                      <TableRow key={r.service_id} data-testid={`bulk-prev-svc-${r.service_id}`}>
                        <TableCell className="text-xs">{r.service_name}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums">${r.old_price.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums font-semibold text-emerald-700">${r.new_price.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div>
                <p className="text-sm font-medium mb-1">Precios propios en fichas de residentes</p>
                {bulkPreview.assignments.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Ningún residente tiene precio propio para estos servicios: todos facturan el precio del catálogo.</p>
                ) : (
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead className="text-xs">Residente</TableHead>
                      <TableHead className="text-xs">Servicio</TableHead>
                      <TableHead className="text-xs text-right">Precio actual</TableHead>
                      <TableHead className="text-xs text-right">Precio nuevo</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {bulkPreview.assignments.map(r => (
                        <TableRow key={r.assignment_id} data-testid={`bulk-prev-asg-${r.assignment_id}`}>
                          <TableCell className="text-xs">{r.resident_name}</TableCell>
                          <TableCell className="text-xs">{r.service_name}</TableCell>
                          <TableCell className="text-xs text-right tabular-nums">${r.old_price.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</TableCell>
                          <TableCell className="text-xs text-right tabular-nums font-semibold text-emerald-700">${r.new_price.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </div>
          ) : (
          <div className="space-y-3">
            <div><Label>% a aplicar (positivo o negativo) *</Label>
              <Input type="number" step="0.01" placeholder="Ej. 15 para +15% o -10 para -10%" value={bulkDlg.pct} onChange={(e) => setBulkDlg({ ...bulkDlg, pct: e.target.value })} data-testid="bulk-pct-input" /></div>
            <div><Label>Redondear a múltiplo de (opcional)</Label>
              <Input type="number" placeholder="Ej. 100 para redondear a centenas" value={bulkDlg.round_to} onChange={(e) => setBulkDlg({ ...bulkDlg, round_to: e.target.value })} /></div>
            <div><Label>Motivo</Label><Input value={bulkDlg.reason} onChange={(e) => setBulkDlg({ ...bulkDlg, reason: e.target.value })} /></div>
            <div><Label>Vigente desde (opcional)</Label>
              <Input type="date" value={bulkDlg.effective_from || ''}
                     onChange={(e) => setBulkDlg({ ...bulkDlg, effective_from: e.target.value })}
                     data-testid="bulk-effective-from" />
              <p className="text-[11px] text-muted-foreground mt-1">
                Vacío o fecha de hoy = se aplica ahora. Con una <b>fecha futura</b> el ajuste queda
                programado y se aplica solo a las 00:20 de ese día, así podés cargarlo con anticipación
                sin que afecte lo que facturás mientras tanto.
              </p>
            </div>
            <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={bulkDlg.apply_to_all} onChange={(e) => setBulkDlg({ ...bulkDlg, apply_to_all: e.target.checked })} /> Aplicar a todos los servicios activos</label>
            {!bulkDlg.apply_to_all && insurers.length > 0 && (
              <div className="border rounded p-2 text-sm" data-testid="bulk-insurers">
                <p className="text-xs font-medium mb-1">Por convenio <span className="text-muted-foreground">(toma los conceptos vinculados a cada uno)</span></p>
                <div className="flex flex-wrap gap-3">
                  {insurers.map(os => (
                    <label key={os.insurer_id} className="flex items-center gap-2 text-xs">
                      <input type="checkbox" checked={(bulkDlg.insurer_ids || []).includes(os.insurer_id)}
                             onChange={(e) => {
                               const ids = e.target.checked
                                 ? [...(bulkDlg.insurer_ids || []), os.insurer_id]
                                 : (bulkDlg.insurer_ids || []).filter(i => i !== os.insurer_id);
                               setBulkDlg({ ...bulkDlg, insurer_ids: ids });
                             }}
                             data-testid={`bulk-insurer-${os.insurer_id}`} />
                      {os.name} <span className="text-muted-foreground">({(os.service_ids || []).length} concepto/s)</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {!bulkDlg.apply_to_all && (
              <div className="border rounded p-2 max-h-48 overflow-y-auto text-sm">
                <p className="text-xs font-medium mb-1">O por concepto</p>
                {list.filter(s => s.is_active).map(s => (
                  <label key={s.service_id} className="flex items-center gap-2 py-1">
                    <input type="checkbox" checked={bulkDlg.service_ids.includes(s.service_id)} onChange={(e) => {
                      const ids = e.target.checked ? [...bulkDlg.service_ids, s.service_id] : bulkDlg.service_ids.filter(i => i !== s.service_id);
                      setBulkDlg({ ...bulkDlg, service_ids: ids });
                    }} /> {s.name} <span className="text-xs text-muted-foreground">${s.current_price}</span>
                  </label>
                ))}
              </div>
            )}
            <p className="text-[11px] text-muted-foreground">
              El ajuste se aplica al catálogo y también a los <b>precios propios</b> cargados en las fichas
              de residentes (las fichas archivadas no se tocan). Vas a poder revisarlo antes de confirmar.
            </p>
          </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => bulkPreview ? setBulkPreview(null) : closeBulk()}>
              {bulkPreview ? 'Volver' : 'Cancelar'}
            </Button>
            {bulkPreview ? (
              <Button onClick={applyBulk} disabled={saving} data-testid="apply-bulk-btn">
                {saving ? 'Aplicando...' : 'Confirmar y aplicar'}
              </Button>
            ) : (
              <Button onClick={previewBulk} disabled={saving} data-testid="preview-bulk-btn">
                {saving ? 'Calculando...' : 'Ver vista previa'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Historial */}
      <Dialog open={historyDlg.open} onOpenChange={(o) => !o && setHistoryDlg({ open: false, list: [], service: null })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Historial de precios: {historyDlg.service?.name}</DialogTitle></DialogHeader>
          <Table>
            <TableHeader><TableRow><TableHead>Fecha</TableHead><TableHead>Anterior</TableHead><TableHead>Nuevo</TableHead><TableHead>%</TableHead><TableHead>Motivo</TableHead><TableHead>Usuario</TableHead></TableRow></TableHeader>
            <TableBody>
              {historyDlg.list.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">Sin historial</TableCell></TableRow> :
                historyDlg.list.map(h => (
                  <TableRow key={h.history_id}>
                    <TableCell className="text-xs">{h.changed_at && fmtDateTime(h.changed_at)}</TableCell>
                    <TableCell>${(h.old_price || 0).toLocaleString('es-AR')}</TableCell>
                    <TableCell className="font-semibold">${(h.new_price || 0).toLocaleString('es-AR')}</TableCell>
                    <TableCell className={h.delta_pct >= 0 ? 'text-emerald-700' : 'text-rose-700'}>{h.delta_pct >= 0 ? '+' : ''}{h.delta_pct.toFixed(2)}%</TableCell>
                    <TableCell className="text-xs">{h.reason || '—'}</TableCell>
                    <TableCell className="text-xs">{h.changed_by_name}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Services;
